import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  status: 'idle',
  error: null
};

const readDeviceInfo = () => {
  if (typeof navigator !== 'undefined') {
    return navigator.userAgent;
  }
  return 'Unknown Device';
};

const parseUserFromResponse = (data, fallbackUsername) => {
  if (data?.user) {
    return {
      username: data.user.username || fallbackUsername || data.user.email || null,
      name: data.user.name || data.user.username || fallbackUsername || '',
      email: data.user.email || ''
    };
  }
  return {
    username: fallbackUsername || null,
    name: fallbackUsername || 'Administrator',
    email: data?.email || ''
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  let data;
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && (data.resultDesc || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  return data;
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password, deviceInfo }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          password,
          device_info: deviceInfo || readDeviceInfo()
        })
      });

      const data = await handleApiResponse(response);
      const user = parseUserFromResponse(data, username);

      const expiresIn = Number(data.expires_in || 0);

      return {
        user,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : null
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to login right now.');
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshAccessToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.refreshToken) {
        throw new Error('Refresh token not available.');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: auth.refreshToken,
          device_info: readDeviceInfo()
        })
      });

      const data = await handleApiResponse(response);
      const expiresIn = Number(data.expires_in || 0);

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token || auth.refreshToken,
        expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : auth.expiresAt
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to refresh session.');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { getState }) => {
    const { auth } = getState();

    try {
      if (auth.refreshToken) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(auth.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : {})
          },
          body: JSON.stringify({
            refresh_token: auth.refreshToken
          })
        });
      }
    } catch (error) {
      // Even if logout request fails, we proceed to clear client state
      console.error('Logout request failed:', error);
    }

    return null;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => ({
      ...initialState
    })
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.expiresAt = action.payload.expiresAt;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Unable to login right now.';
      })
      .addCase(refreshAccessToken.pending, (state) => {
        state.status = state.status === 'loading' ? 'loading' : 'refreshing';
        state.error = null;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.expiresAt = action.payload.expiresAt;
        state.error = null;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Unable to refresh session.';
      })
      .addCase(logoutUser.fulfilled, () => ({
        ...initialState
      }))
      .addCase(logoutUser.rejected, () => ({
        ...initialState
      }));
  }
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
